/**
 * EventParticipantsPanel - Manage vendors/participants for a pop-up event
 * Handles entity search, stub creation, and participant list management
 * @atomic @idempotent
 */
import { memo, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { eventsAPI } from '../../services/api/events'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import { getInputClasses, getSelectClasses } from '../../styles/inputStyles'

const logger = createLogger('EventParticipantsPanel')

const EventParticipantsPanel = memo(function EventParticipantsPanel({
  eventId,
  initialParticipants = [],
  onDone,
}) {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()

  const [participants, setParticipants] = useState(initialParticipants)
  const [participantSearch, setParticipantSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [stubName, setStubName] = useState('')
  const [stubCategory, setStubCategory] = useState('market')
  const [showStubForm, setShowStubForm] = useState(false)

  useEffect(() => { setParticipants(initialParticipants) }, [initialParticipants])

  const handleParticipantSearch = useCallback(async (query) => {
    setParticipantSearch(query)
    if (query.length < 2) { setSearchResults([]); return }
    setIsSearching(true)
    await execute(
      () => eventsAPI.searchEntities(query),
      {
        onSuccess: (res) => {
          const existing = new Set(participants.map(p => p.entityId))
          setSearchResults((res.data?.data || []).filter(e => !existing.has(e.id)))
        },
        onError: () => setSearchResults([])
      }
    )
    setIsSearching(false)
  }, [execute, participants])

  const handleAddExisting = useCallback(async (entity) => {
    await execute(
      () => eventsAPI.adminAddParticipant(eventId, { entityId: entity.id }),
      {
        onSuccess: (res) => {
          setParticipants(prev => [...prev, {
            id: res.data?.participantId,
            entityId: entity.id,
            displayName: entity.name,
            isStub: false,
            category: entity.category,
          }])
          setSearchResults([])
          setParticipantSearch('')
          toast.success(t('admin.events.participantAdded'))
        },
        onError: (err) => toast.error(err.message)
      }
    )
  }, [eventId, execute, t])

  const handleAddStub = useCallback(async () => {
    if (!stubName.trim()) return
    await execute(
      () => eventsAPI.adminAddParticipant(eventId, { stubName: stubName.trim(), stubCategory }),
      {
        onSuccess: (res) => {
          setParticipants(prev => [...prev, {
            id: res.data?.participantId,
            entityId: res.data?.entityId,
            displayName: stubName.trim(),
            isStub: true,
            category: stubCategory,
          }])
          setStubName('')
          setShowStubForm(false)
          toast.success(t('admin.events.stubAdded'))
        },
        onError: (err) => { logger.error('Failed to add stub', err); toast.error(err.message) }
      }
    )
  }, [stubName, stubCategory, eventId, execute, t])

  const handleRemove = useCallback(async (participant) => {
    await execute(
      () => eventsAPI.adminRemoveParticipant(eventId, participant.entityId),
      {
        onSuccess: () => {
          setParticipants(prev => prev.filter(p => p.entityId !== participant.entityId))
          toast.success(t('admin.events.participantRemoved'))
        },
        onError: (err) => toast.error(err.message)
      }
    )
  }, [eventId, execute, t])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
      <h3 className="font-semibold text-gray-800">{t('admin.events.section.participants')} ({participants.length})</h3>

      {/* Search existing entity */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          value={participantSearch}
          onChange={(e) => handleParticipantSearch(e.target.value)}
          className={getInputClasses(false) + ' pl-9'}
          placeholder={t('admin.events.searchEntityPlaceholder')}
        />
        {isSearching && (
          <div className="absolute right-3 top-2.5 animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
        )}
        {searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {searchResults.map(entity => (
              <button
                key={entity.id}
                onClick={() => handleAddExisting(entity)}
                onTouchEnd={() => handleAddExisting(entity)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
              >
                <span className="font-medium text-sm">{entity.name}</span>
                <span className="text-xs text-gray-400">{entity.category}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick-create stub */}
      <div>
        <button
          onClick={() => setShowStubForm(s => !s)}
          onTouchEnd={() => setShowStubForm(s => !s)}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
        >
          <UserPlusIcon className="h-4 w-4" />
          {t('admin.events.addStub')}
        </button>
        {showStubForm && (
          <div className="mt-2 flex gap-2">
            <input
              value={stubName} onChange={(e) => setStubName(e.target.value)}
              className={getInputClasses(false) + ' flex-1'}
              placeholder={t('admin.events.stubNamePlaceholder')}
            />
            <select value={stubCategory} onChange={(e) => setStubCategory(e.target.value)}
              className={getSelectClasses()}>
              <option value="market">market</option>
              <option value="food_truck">food_truck</option>
              <option value="coffee">coffee</option>
              <option value="ice_cream">ice_cream</option>
              <option value="retail">retail</option>
              <option value="other">other</option>
            </select>
            <button onClick={handleAddStub} onTouchEnd={handleAddStub}
              className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm">
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Participant list */}
      {participants.length === 0 ? (
        <p className="text-sm text-gray-400">{t('admin.events.noParticipants')}</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {participants.map(p => (
            <li key={p.entityId} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">{p.displayName}</span>
                {p.isStub && (
                  <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">stub</span>
                )}
                {p.category && <span className="text-xs text-gray-400">{p.category}</span>}
              </div>
              <button
                onClick={() => handleRemove(p)}
                onTouchEnd={() => handleRemove(p)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={onDone}
        onTouchEnd={onDone}
        className="w-full py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
      >
        {t('admin.events.done')}
      </button>
    </div>
  )
})

export default EventParticipantsPanel
